import { type FC } from 'react'
import styles from './InvitationEmailPreview.module.scss'
import { Button } from '../../../components/Button/Button'
import { type BookingClient, type SchedulingEventType } from '../../../models/Scheduling'
import { clientDisplayName } from '../shared/schedulingDisplay'

interface InvitationEmailPreviewProps {
  eventType: SchedulingEventType
  client?: BookingClient
  ownerName: string
  onEdit: () => void
  onCopyEmail: () => void
  copyDisabled?: boolean
}

// Renders into the parent's two-column grid (label cell + value cell)
const EmailField: FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <>
    <div className='paragraph-small zocks-text-gray-500'>{label}</div>
    <div className='paragraph-small'>{children}</div>
  </>
)

// Right column for the "Let the client choose" mode: the invitation email the client receives,
// with the booking page link.
export const InvitationEmailPreview: FC<InvitationEmailPreviewProps> = ({ eventType, client, ownerName, onCopyEmail, copyDisabled = false }) => {
  const firstName = client?.firstName ?? client?.email ?? 'there'

  return (
    <div className='d-flex flex-column zocks-gap-0.5'>
      <h5 className='mb-0'>Invitation email</h5>
      <div className='paragraph-small zocks-text-gray-600'>This email gets sent to clients with the booking page link.</div>
      <div className='d-flex flex-column mt-2 border zocks-border-color-gray-200 zocks-border-1.0 overflow-hidden zocks-bg-color-white'>
        <div className={`p-3 zocks-bg-color-gray-100 ${styles.fields}`}>
          <EmailField label='From'>
            {ownerName} via Zocks <span className='zocks-text-gray-500'>· bookings@zocks.io</span>
          </EmailField>
          <EmailField label='To'>
            {client ? (
              <>
                {clientDisplayName(client)} <span className='zocks-text-gray-500'>· {client.email}</span>
              </>
            ) : (
              <span className='zocks-text-gray-500'>Your client</span>
            )}
          </EmailField>
          <EmailField label='Subject'>You&apos;re invited to book a meeting</EmailField>
        </div>
        <div className='d-flex flex-column gap-3 p-3'>
          <div className='paragraph-medium'>Hi {firstName},</div>
          <div className='paragraph-medium'>
            I&apos;d love to schedule an <span className='bold'>{eventType.name}</span> with you ({eventType.durationMinutes} min). Pick a time that works for
            you.
          </div>
          <div>
            <Button variant='primary' type='button'>
              Book your time
              <i className='bi bi-chevron-right ms-1' />
            </Button>
          </div>
          <div className='paragraph-medium'>Feel free to reach out if you have any questions,</div>
          <div className='d-flex flex-column'>
            <div className='paragraph-medium'>{ownerName}</div>
            <div className='paragraph-small zocks-text-gray-600'>Financial Advisor</div>
          </div>
          <div className='d-flex zocks-gap-0.5'>
            <Button variant='secondary' type='button' disabled={copyDisabled} onClick={onCopyEmail}>
              <i className='bi bi-copy me-1' />
              Copy email
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}
